import {
  configAffiliateShopping,
  configTeaser,
  DataSourceWithConfig,
} from "@pub/config";
import placeholders from "@pub/placeholders";
import { Ads, Card, ContentBlock } from "@sphtech/dmg-design-system";
import ProductDeals from "@src/app/components/Modules/Product/ProductDeals";
import { AffiliateShoppingDataBlock } from "@src/app/types/ServerCustomContext";
import { mapTeaserDataToAnchor } from "@src/app/utils/mapTeaserDataToProps";
import { getVariantByIndex } from "@src/app/utils/VariantByIndex";
import cx from "classnames";
import { TextLink } from "packages/design-system/src/components/Element/Typography";
import { ReactElement } from "react";

import styles from "../affiliateshopping.module.css";

type ShopTheEditProps = {
  data: AffiliateShoppingDataBlock;
  dataSource?: DataSourceWithConfig;
  withDivider?: boolean;
};

export default function ShopTheEdit({
  data,
  dataSource,
  withDivider,
}: ShopTheEditProps): ReactElement {
  const datasourceConfig = dataSource?.config;
  const anchorBlockLayout = datasourceConfig?.anchorBlock?.cardLayout;
  const { title, titleZH, code, teasers, url } = data;
  const stories = teasers?.filter((teaser) => !teaser.deals);
  const anchorBlock =
    anchorBlockLayout && stories?.[0]
      ? mapTeaserDataToAnchor(stories[0], code)
      : undefined;
  const products = teasers?.filter((teaser) => teaser.deals);
  const moreStories = stories?.slice(anchorBlockLayout ? 1 : 0, 12);
  const ctaLinkProducts = url ?? anchorBlock?.path;
  const moreStoriesConfig = datasourceConfig?.moreStories;

  return (
    <>
      {moreStoriesConfig?.ads?.leaderboard && <Ads.AdsMidContent />}

      <ContentBlock
        heading={title}
        headingZH={titleZH}
        description={datasourceConfig?.desc}
        inlineGutter
        withDivider={withDivider}
      >
        {anchorBlockLayout && anchorBlock && (
          <Card
            {...anchorBlock}
            variant={anchorBlockLayout.defaultVariant}
            mobileUpVariant={anchorBlockLayout.mobileUp.defaultVariant}
            placeholders={placeholders}
            displayPublishDate={false}
          />
        )}

        {products && products.length > 0 && (
          <div
            className={cx(
              styles.content,
              datasourceConfig?.productMobileLayout === "list" &&
                styles.productListContainer,
            )}
          >
            <div
              className={cx(
                styles.contentProductsContainer,
                styles.contentFeaturedProducts,
                styles[datasourceConfig?.productMobileLayout || "list"],
              )}
            >
              <ProductDeals
                data={{
                  items: products,
                }}
                isCarousel={false}
                options={{
                  sourceNameAsLabel: true,
                  discountPercentageBesidePrice: true,
                  imageCropType: "square",
                  hasBadge: true,
                  hasHeaderTitle: true,
                }}
              />

              {ctaLinkProducts && (
                <div className={cx(styles.ctaContainer)}>
                  <TextLink href={ctaLinkProducts}>
                    {datasourceConfig?.cta?.name || "View More"}
                    {configAffiliateShopping.viewMore?.icon}
                  </TextLink>
                </div>
              )}
            </div>

            <div className={styles.adsContainer}>
              <Ads.AdsSideBar />
            </div>
          </div>
        )}

        {moreStories && moreStories.length > 0 && (
          <div className={styles.moreStoriesContainer}>
            <div
              className={cx(
                styles.moreStories,
                moreStoriesConfig?.ads?.sidebar && styles.hasAds,
                styles[moreStoriesConfig?.layout?.mobileUp || "list"],
              )}
            >
              {moreStories.map((args, index) => {
                return (
                  <Card
                    key={index}
                    {...args}
                    variant={
                      datasourceConfig?.moreStories?.cardLayout
                        ?.defaultVariant || "card-stacked-landscape"
                    }
                    mobileUpVariant={
                      moreStoriesConfig?.cardLayout?.mobileUp
                        ? getVariantByIndex(
                            moreStoriesConfig.cardLayout.mobileUp,
                            index,
                          )
                        : "card-list-landscape-mobile-up"
                    }
                    displayPublishDate={false}
                    dateAfter={configTeaser.dateAfter}
                  />
                );
              })}
            </div>
            {moreStoriesConfig?.ads?.sidebar && (
              <div>
                <Ads.AdsSideBar />
              </div>
            )}

            {moreStoriesConfig?.hasCta && url && (
              <div className={cx(styles.ctaContainer)}>
                <TextLink href={url}>
                  {datasourceConfig?.cta?.name || "View More"}
                  {configAffiliateShopping.viewMore?.icon}
                </TextLink>
              </div>
            )}
          </div>
        )}
      </ContentBlock>
    </>
  );
}
